import React, { useCallback, useContext, useState } from "react";

import { faListAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { GROUP_BY_ASSIGNEE, GROUP_BY_CUSTOM, GROUP_BY_NONE, GROUP_BY_STREAM, Views } from "~/actionItems/constants";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import Menu from "~/ui/menu";

import styles from "./Filters.module.css";

export type GroupByType = "CUSTOM" | "DUE_DATE" | "STREAM" | "ASSIGNEE" | "NONE";

export const GROUP_BY_NAMES: { [key in GroupByType]: string } = {
	CUSTOM: "Custom",
	ASSIGNEE: "Assignee",
	DUE_DATE: "Due Date",
	STREAM: "Note Series",
	NONE: "None",
};

interface GroupByOptionProps {
	type: GroupByType;
}

export function GroupByOption({ type }: GroupByOptionProps) {
	const { filters, view } = useContext(ActionItemsContext);
	const { onChangeGroupBy } = filters;

	const onSelect = useCallback(() => onChangeGroupBy(type), [type, onChangeGroupBy]);

	// Custom sorting are only available for CUSTOM and NONE groups
	if (view === Views.TEAMMATES && (type === GROUP_BY_CUSTOM || type === GROUP_BY_NONE)) {
		return null;
	} else if (view === Views.USER && type === GROUP_BY_ASSIGNEE) {
		return null;
	} else if (view === Views.STREAM_TAB && (type === GROUP_BY_CUSTOM || type === GROUP_BY_STREAM)) {
		return null;
	}

	return (
		<Menu.RadioItem value={type} onSelect={onSelect}>
			{GROUP_BY_NAMES[type]}
		</Menu.RadioItem>
	);
}

export default function GroupBy() {
	const { filters } = useContext(ActionItemsContext);
	const { groupBy } = filters;

	const [isOpen, setIsOpen] = useState<boolean>(false);

	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);

	return (
		<Menu open={isOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger>
				<button className={cx(styles.filterButton, { [styles.active]: isOpen })}>
					<FontAwesomeIcon icon={faListAlt} fixedWidth />
					Grouping{groupBy !== "NONE" ? `: ${GROUP_BY_NAMES[groupBy].toLowerCase()}` : ""}
					<FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
				</button>
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Label>Group by</Menu.Label>
				<Menu.RadioGroup value={groupBy}>
					{Object.keys(GROUP_BY_NAMES).map(groupByType => (
						<GroupByOption key={groupByType} type={groupByType as GroupByType} />
					))}
				</Menu.RadioGroup>
			</Menu.Content>
		</Menu>
	);
}
